import { valibotResolver } from "@hookform/resolvers/valibot";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeftToLineIcon, ArrowRightToLineIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, type FieldErrors } from "react-hook-form";

import { Button } from "@/web/components/primitives/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/web/components/primitives/tabs";
import { toaster } from "@/web/components/primitives/toast";
import { FullPageLoader } from "@/web/components/styled/FullPageLoader";
import { Redirect } from "@/web/components/Redirect";
import { formatCanonicalAddressFull } from "@/web/lib/addresses";
import { ErrorHandler } from "@/web/lib/errors";
import { useFeatureFlag } from "@/web/lib/featureFlags";
import { cn } from "@/web/lib/styling";
import { tableQueryOptions, useProcedureMutation, useProcedureQueryLazy } from "@/web/lib/tanstack";
import { brandIndexSearch } from "@/web/routes/_protected/smb/-dashboard/brandSelection";
import { InvestmentSidebar } from "@/web/routes/ttw/-page/InvestmentSidebar";
import { TtwPage } from "@/web/routes/ttw/-page/TtwPage";
import { bpsToPercent, wireCoordinates, type TtwPageContent } from "@/web/routes/ttw/-page/content";
import { procedures, tables } from "@tycho/web-bindings";
import type { Uuid } from "spacetimedb";

import { OverviewTab } from "./-ttw-composer/OverviewTab";
import { RealEstateTab } from "./-ttw-composer/RealEstateTab";
import { SettingsTab } from "./-ttw-composer/SettingsTab";
import { StewardTab } from "./-ttw-composer/StewardTab";
import {
  composerSchema,
  formToContent,
  propertySqftFromForm,
  resultToForm,
  targetPropertyFromForm,
  validateCrossFields,
  type ComposerForm,
} from "./-ttw-composer/formModel";

export const Route = createFileRoute("/_protected/profile/ttw-editor")({
  component: TtwEditorRoute,
  // `?brand=N` names WHICH of the caller's brands is being edited, carried over
  // from the dashboard's brand switcher. Load-bearing for multi-brand owners:
  // without it this route fell back to the server's "first live brand", so a
  // brand-B admin edited — and saved over — brand A's page. See
  // `brandSelection.tsx` and `resolveBrandForCaller` on the server.
  validateSearch: brandIndexSearch,
});

// ---------------------------------------------------------------------------
// The TTW page composer.
//
// A 360px editing rail pushes (not overlays) a LIVE preview of the public page.
// The preview is the real `<TtwPage>` from `routes/ttw/-page/` fed from
// in-progress form values — there is no second renderer, so what the owner sees
// while typing is the page itself.
//
// Live data stays LIVE in the editor: the sidebar's backer feed, backer count,
// and nominator credit read `getTtwPageBackers` against the owner's real card
// rather than being stubbed. An owner previewing their page sees the actual
// community activity on it.
//
// Saving is explicit. `saveTtwPage` replaces the highlights/key-stats/gallery
// child lists wholesale on every call, so firing it per keystroke or per blur
// would mean repeated full rewrites of three tables; a single Save keeps writes
// proportional to intent and gives failures somewhere to surface.
// ---------------------------------------------------------------------------

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "steward", label: "Steward" },
  { id: "real-estate", label: "Real Estate" },
  { id: "settings", label: "Settings" },
] as const;

type TabId = (typeof TABS)[number]["id"];

/** Which tab owns each field, so a validation error can jump the owner to it. */
const FIELD_TAB: Partial<Record<keyof ComposerForm, TabId>> = {
  featureImage: "overview",
  pitchVideoUpload: "overview",
  pitchVideoUrl: "overview",
  businessName: "overview",
  category: "overview",
  highlights: "overview",
  stewardQuote: "steward",
  stewardAbout: "steward",
  keyStats: "steward",
  gallery: "real-estate",
  targetProperty: "real-estate",
  targetArea: "real-estate",
  realEstateAbout: "real-estate",
  propertyType: "real-estate",
  propertySqft: "real-estate",
  stories: "real-estate",
  yearBuilt: "real-estate",
  accessCode: "settings",
  showTotalReserved: "settings",
  showAppreciationShare: "settings",
};

function TtwEditorRoute() {
  const ttwComposer = useFeatureFlag("TTW_COMPOSER");
  if (!ttwComposer) return <Redirect to="/$" />;
  return <TtwEditorBrandGate />;
}

/**
 * Resolve `?brand=N` to a real brand id before the editor mounts.
 *
 * This gate exists because the brand is not optional context here — it decides
 * which page is read AND which card is written. Rendering the composer before
 * the brand list has loaded would start it on the server's default brand, seed
 * the form from that page, and leave a Save button that commits those values
 * onto the wrong card. So we hold the loader until the list settles, then mount
 * the editor keyed on the resolved id.
 *
 * The `key` is what makes switching brands safe: the composer holds a
 * seeded-once `react-hook-form` (see `seeded` below), so remounting is the only
 * way to re-seed it against a different brand's page. Without the key, changing
 * `?brand=` would swap the underlying data while leaving brand A's values in the
 * form — and the next Save would write them to brand B.
 */
function TtwEditorBrandGate() {
  const { brand: brandIndex } = Route.useSearch();
  const brandsQuery = useQuery(tableQueryOptions(tables.allBrandsForUser));

  if (brandsQuery.isLoading) return <FullPageLoader />;

  const brands = brandsQuery.data ?? [];
  // An out-of-range index (stale bookmark, a brand since removed) falls back to
  // the first brand rather than erroring — same lenient stance as
  // `brandIndexSearch`. `undefined` when the user has no brands at all, which
  // leaves the server on its own resolution and its "finish setting up your
  // business" message.
  const brand = (brandIndex !== undefined ? brands[brandIndex] : brands[0]) ?? brands[0];

  return <TtwEditor key={brand?.id.toString() ?? "none"} brand={brand?.id} />;
}

function TtwEditor({ brand }: { brand: Uuid | undefined }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>("overview");
  const [collapsed, setCollapsed] = useState(false);

  // Echoed back onto the dashboard link so the round trip preserves the brand.
  // Read from the URL rather than recomputed from `brand` so it round-trips
  // exactly what got us here (including the absent-means-first-brand case).
  const brandSearch = Route.useSearch();

  // objectPath → session `blob:` URL, so a just-uploaded asset previews before
  // it's necessarily reachable on the CDN. A ref (not state) because mutating it
  // shouldn't itself re-render; the form change that follows every upload does.
  const blobsRef = useRef(new Map<string, string>());
  const [blobVersion, setBlobVersion] = useState(0);
  const onAssetUploaded = useCallback((objectPath: string, previewUrl: string) => {
    blobsRef.current.set(objectPath, previewUrl);
    setBlobVersion((v) => v + 1);
  }, []);

  // `brand` is part of the query key, so switching brands can't serve the other
  // brand's cached page while the new one loads.
  const pageQuery = useProcedureQueryLazy(procedures.getMyTtwPage, { brand });
  const propertiesQuery = useQuery(tableQueryOptions(tables.allTargetablePropertiesForUser));
  // The Settings tab edits card columns (`accessCode`, `browsable`) that
  // `getMyTtwPage` deliberately does NOT return — that read is shared with the
  // anonymous public page, so the access code must never travel on it (see
  // `docs/TTW_PAGE_V2.md` Trap 4). The owner's own dashboard subscription
  // carries the full card row, which is where the share drawer reads it too.
  const superViewQuery = useQuery(tableQueryOptions(tables.smbSuperView));
  const { mutateAsync: savePage, isPending: isSaving } = useProcedureMutation(
    procedures.saveTtwPage,
  );

  const form = useForm<ComposerForm>({
    resolver: valibotResolver(composerSchema),
    // Rehydrated below once the query settles; RHF needs *some* defaults now.
    defaultValues: resultToForm(undefined),
  });

  // The card whose settings the Settings tab edits — matched BY ID against the
  // one `getMyTtwPage` resolved, never re-derived client-side.
  //
  // This must be the exact card `saveTtwPage` will write to, and the two resolve
  // it through different code: the server walks first live brand link → first
  // LIVE entity → its live card, while `smbSuperView` emits a row per entity
  // WITHOUT filtering soft-deleted ones. An owner whose first entity is
  // soft-deleted would get a different card from each. Seeding from card A and
  // saving to card B means an ordinary content save silently rewrites B's
  // password and browse visibility with A's values.
  //
  // Matching on `pageQuery.data.cardId` removes the guess: the server tells us
  // which card it picked, in every branch where one exists (including before the
  // first save).
  const resolvedCardId = pageQuery.data?.cardId;
  const savedCard = useMemo(() => {
    if (!resolvedCardId) return undefined;
    for (const entity of superViewQuery.data ?? []) {
      const match = entity.businessCards
        .map((bc) => bc.businessCard)
        // `.compareTo(...) === 0`, not `===`: `Uuid` is a class instance, so
        // reference equality is always false for two rows carrying the same id.
        .find((bc) => bc.deletedAt === undefined && bc.id.compareTo(resolvedCardId) === 0);
      if (match) return match;
    }
    return undefined;
  }, [superViewQuery.data, resolvedCardId]);

  // Seed the form exactly once, when the saved page first arrives. Keyed on the
  // query's settled state rather than an effect on `data` so a background
  // refetch can't clobber in-progress edits.
  //
  // Waits on the CARD query too, not just the page: the Settings fields seed
  // from the card, and seeding before it lands would show platform defaults
  // ("no password", "browsable") for a card that is actually protected — and
  // then the next Save would write those defaults over the owner's real
  // settings. Both writes go out on every Save, so a wrong seed is a silent
  // data change, not just a wrong-looking toggle.
  const [seeded, setSeeded] = useState(false);
  const cardSettled = !superViewQuery.isLoading;
  if (!seeded && !pageQuery.isLoading && pageQuery.data && cardSettled) {
    form.reset(resultToForm(pageQuery.data, savedCard));
    setSeeded(true);
  }

  // The server resolved a card, but we couldn't find its row to seed from. The
  // Settings fields are therefore showing platform defaults for a card whose
  // real values we don't know — and every Save writes those two columns, so
  // saving now would overwrite the owner's password/visibility with guesses.
  //
  // Same stance as `propertiesReady` below: a value we can't resolve is not the
  // same as a value the owner cleared, so block the save rather than write a
  // silent default. Expected to be unreachable (both sides resolve the same
  // card); this exists so that if they ever diverge again the failure is a
  // blocked save, not a quiet data loss.
  const settingsReady = resolvedCardId === undefined || savedCard !== undefined;

  const values = form.watch();
  // `all_targetable_properties_for_user` returns the catalogs of EVERY brand the
  // user belongs to (the view says so, and each row carries its `brand`), so a
  // multi-brand owner would otherwise be offered another business's buildings in
  // this page's property selector.
  //
  // Fails CLOSED on an unresolved brand: showing every brand's buildings is the
  // one outcome this filter exists to prevent, and `saveTtwPage` would reject
  // them anyway. An owner with no brand at all has nothing to choose from.
  const brandCatalog = useMemo(() => {
    const all = propertiesQuery.data ?? [];
    if (!brand) return [];
    return all.filter((p) => p.brand.compareTo(brand) === 0);
  }, [propertiesQuery.data, brand]);

  // The catalog is NOT the set of valid choices. It holds every property the
  // brand ever browsed or added; `saveTtwPage` accepts only the entity's live
  // `uw_entities_to_targetable_properties` links, which are capped at 3 and drop
  // on untarget. Offering the difference meant the selector listed buildings the
  // server refused with "That property isn't one of your target properties" —
  // and, once a page was saved against one, untargeting it elsewhere in the app
  // left the page permanently unsaveable with no hint why.
  //
  // `eligibleTargetProperties` is resolved server-side through the very accessor
  // `saveTtwPage` validates against, so the two cannot drift.
  const eligibleIds = useMemo(
    () => new Set((pageQuery.data?.eligibleTargetProperties ?? []).map((id) => id.toString())),
    [pageQuery.data],
  );
  const properties = useMemo(
    () => brandCatalog.filter((p) => eligibleIds.has(p.id.toString())),
    [brandCatalog, eligibleIds],
  );

  // A saved selection that is no longer targeted. Resolved against the CATALOG
  // (not the offered list) so we can name the building in the warning — the row
  // still exists, it just stopped being a legal choice. Surfacing this up front
  // beats letting the owner discover it by pressing Save.
  const staleTargetProperty = useMemo(() => {
    if (values.targetProperty.length === 0) return undefined;
    if (eligibleIds.has(values.targetProperty)) return undefined;
    return brandCatalog.find((p) => p.id.toString() === values.targetProperty);
  }, [values.targetProperty, eligibleIds, brandCatalog]);

  // The property list is REQUIRED to save, not merely decorative: the form holds
  // a target property as a string id, and `targetPropertyFromForm` resolves it
  // by looking it up in this list. Saving against an unloaded (empty) list
  // resolves a persisted selection to `undefined`, which either trips the
  // property-or-area check or — worse, when `targetArea` is also filled —
  // silently drops the page's property association. A missing lookup is
  // indistinguishable from "the owner chose none", so the fix is to not save
  // until the list is available.
  const propertiesReady = !propertiesQuery.isLoading && !propertiesQuery.isError;

  // Resolved against the catalog, not the offered list: when the selection has
  // gone stale the preview should still draw the building the page currently
  // points at. The preview's job is to show what is saved; the selector's is to
  // show what may be chosen.
  const selectedProperty = brandCatalog.find((p) => p.id.toString() === values.targetProperty);
  const propertyAddress = selectedProperty
    ? formatCanonicalAddressFull(selectedProperty.canonicalAddress)
    : undefined;
  // Coordinates ride along on the property row (resolved when it was added), so
  // the preview's map pins the real building without geocoding anything.
  // Memoized on the two scalars rather than recomputed inline: `wireCoordinates`
  // returns a fresh object each call, which would make it a new dependency on
  // every render and defeat the `content` memo below.
  const latitude = selectedProperty?.latitude;
  const longitude = selectedProperty?.longitude;
  const propertyCoordinates = useMemo(
    () => wireCoordinates(latitude, longitude),
    [latitude, longitude],
  );
  // Zip picks the neighborhood market dataset, so the preview's chart matches
  // what the published page will draw — including the "no data for this
  // neighborhood" state, which the owner should see while choosing a property
  // rather than discovering after publishing. Square footage is NOT read off the
  // property here: it is an editable field now, so it comes off the form (see
  // `formToContent`) and is only seeded from the catalog by the effect below.
  const propertyZip = selectedProperty?.canonicalAddress.zip;

  // Seed the square-footage input from the catalog when the owner picks a
  // property.
  //
  // Two things this must get right, both of which are about not attributing a
  // footprint to the wrong building:
  //
  //  1. It must not overwrite a figure the OWNER typed — that's their
  //     correction, and the catalog is only a suggestion.
  //  2. It MUST overwrite a figure this effect previously seeded, because that
  //     one describes the property they just switched away from. Guarding on
  //     "field is non-empty" alone can't tell those apart, and silently pairs
  //     building A's footprint with building B's address and price series.
  //
  // So we remember the exact string we last wrote: if the field still holds it,
  // it's ours to replace; anything else is the owner's and is left alone. A
  // field the owner deliberately cleared also stays cleared, since an empty
  // string never equals a seeded value.
  const catalogSqft = selectedProperty?.sqft;
  const lastSeededSqft = useRef<string | undefined>(undefined);
  const lastSeededProperty = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (values.targetProperty === lastSeededProperty.current) return;

    // The catalog rides on `propertiesQuery`, which settles INDEPENDENTLY of
    // the page query that seeds the form. Returning before claiming the guard
    // is what makes the seed survive that race: an early return here leaves the
    // effect armed, so it runs again for this same property once the list
    // arrives. Claiming the guard first would burn the one shot on the render
    // where there was nothing to seed yet.
    if (catalogSqft === undefined) return;
    const rounded = Math.round(catalogSqft);
    if (!Number.isFinite(rounded) || rounded <= 0) return;

    lastSeededProperty.current = values.targetProperty;

    // `getValues` rather than `values`: this effect must not re-run when the
    // owner types in the field it writes to.
    const current = form.getValues("propertySqft").trim();
    const ownerAuthored = current.length > 0 && current !== lastSeededSqft.current;
    if (ownerAuthored) return;

    const next = String(rounded);
    lastSeededSqft.current = next;
    form.setValue("propertySqft", next, { shouldDirty: true });
  }, [values.targetProperty, catalogSqft, form]);

  // Offering terms are platform-set and NOT composer-editable. Before the first
  // save there are no stored terms, so fall back to the platform defaults —
  // otherwise a first-time owner would preview "$0/share".
  const saved = pageQuery.data;
  const offering = useMemo(
    () => ({
      sharePrice: saved?.hasPage ? Number(saved.sharePriceCents) / 100 : DEFAULT_SHARE_PRICE,
      totalShares: saved?.hasPage ? Number(saved.totalShares) : DEFAULT_TOTAL_SHARES,
      targetReturnRange: (saved?.hasPage
        ? [bpsToPercent(saved.targetReturnBps), bpsToPercent(saved.targetReturnBpsMax)]
        : [DEFAULT_RETURN_MIN, DEFAULT_RETURN_MAX]) as [number, number],
      targetRaise: saved?.hasPage ? Number(saved.targetRaiseCents) / 100 : 0,
      stage: "planning" as const,
      appreciationSharePercent: saved?.hasPage ? bpsToPercent(saved.appreciationShareBps) : 0,
    }),
    [saved],
  );

  // Drives the preview's share button. Absent before the first save (no card
  // yet), which hides the button rather than offering a link that 404s.
  const shareCardId = saved?.cardId;

  const content = useMemo(
    () =>
      formToContent(values, {
        // `blobVersion` isn't read here — it's a dependency so an upload
        // re-derives the preview against the updated blob map.
        blobs: blobsRef.current,
        propertyAddress,
        propertyCoordinates,
        propertyZip,
        shareCardId,
        offering,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, propertyAddress, propertyCoordinates, propertyZip, shareCardId, offering, blobVersion],
  );

  // Jump to the tab owning the first schema-validation error, so a failure on a
  // tab the owner isn't looking at doesn't read as a dead Save button.
  //
  // Reads the `errors` RHF hands the invalid handler, NOT `form.formState.errors`.
  // `formState` is a Proxy that only keeps the properties you touch DURING RENDER
  // up to date, and nothing here renders `errors` (the fields subscribe their own
  // `fieldState`), so the closure saw a stale empty object and this never fired —
  // the required-feature-image error sat unread on the Overview tab while the
  // owner stared at Real Estate.
  const onInvalid = (errors: FieldErrors<ComposerForm>) => {
    const firstErrored = (Object.keys(errors) as Array<keyof ComposerForm>).find(
      (field) => FIELD_TAB[field] !== undefined,
    );
    const target = firstErrored ? FIELD_TAB[firstErrored] : undefined;
    if (target) setTab(target);
  };

  const onSave = form.handleSubmit(async (data) => {
    // Guard the SAVE, not just the button: `handleSubmit` can be reached by an
    // Enter keypress in any text field, so disabling the button alone would
    // still let a save through with an unresolved property list.
    if (!propertiesReady) {
      toaster({
        message: "Still loading your properties — try again in a moment.",
        variant: "error",
      });
      return;
    }

    // Same reasoning as above, for the card the Settings tab edits: saving with
    // unresolved settings would write defaults over the owner's real password
    // and browse visibility.
    if (!settingsReady) {
      toaster({
        message: "We couldn't load your page settings — reload and try again.",
        variant: "error",
      });
      return;
    }

    // Cross-field rules the flat schema can't express (video mode, and the
    // property-or-area requirement). Mirrors the server, which stays the
    // authority — this just avoids a round trip to learn about it.
    const crossErrors = validateCrossFields(data);
    if (crossErrors.length > 0) {
      for (const [field, message] of crossErrors) {
        form.setError(field, { message });
      }
      const firstField = crossErrors[0]?.[0];
      if (firstField && FIELD_TAB[firstField]) setTab(FIELD_TAB[firstField]);
      return;
    }

    // A chosen property that isn't in the offered list can't be resolved to a
    // Uuid. Silently sending `undefined` would drop the association, so surface
    // it instead — this is a real state (the building was untargeted elsewhere in
    // the app after this page was authored), not just a loading race. The server
    // would reject it too; this just says so in language the owner can act on.
    const targetProperty = targetPropertyFromForm(data.targetProperty, properties);
    if (data.targetProperty.length > 0 && targetProperty === undefined) {
      form.setError("targetProperty", {
        message:
          "This building isn't one of your target properties any more. Choose another, or add it back from the Price panel on your dashboard.",
      });
      setTab("real-estate");
      return;
    }

    try {
      const result = await savePage({
        // The SAME brand the form was seeded from — this is what stops a save
        // from committing one brand's content onto another brand's card.
        brand,
        featureImage: data.featureImage,
        pitchVideoUrl: data.pitchVideoKind === "external" ? data.pitchVideoUrl.trim() : undefined,
        pitchVideoUpload: data.pitchVideoKind === "upload" ? data.pitchVideoUpload : undefined,
        businessName: data.businessName,
        category: data.category,
        targetProperty,
        showNominator: data.showNominator,
        showTotalReserved: data.showTotalReserved,
        showAppreciationShare: data.showAppreciationShare,
        highlights: data.highlights
          .filter((h) => h.text.trim().length > 0)
          .map((h) => ({ text: h.text, icon: h.icon })),
        stewardImage: data.stewardImage || undefined,
        stewardQuote: data.stewardQuote || undefined,
        stewardAttribution: data.stewardAttribution || undefined,
        stewardAbout: data.stewardAbout || undefined,
        keyStats: data.keyStats
          .filter((s) => s.label.trim().length > 0)
          .map((s) => ({ label: s.label, icon: s.icon, value: s.value })),
        realEstateAbout: data.realEstateAbout || undefined,
        targetArea: data.targetArea || undefined,
        gallery: data.gallery,
        // Property attributes are claims about a specific building, so they are
        // dropped outright when the page has none — the renderer hides the whole
        // block in that case (Trap 10), and the composer hides their inputs.
        // Persisting them anyway would leave invisible, uneditable values on the
        // row that would reappear if the owner ever re-picked a property, now
        // describing a DIFFERENT building.
        ...(targetProperty !== undefined
          ? {
              propertyType: data.propertyType || undefined,
              stories: data.stories || undefined,
              yearBuilt: data.yearBuilt || undefined,
              propertySqft: propertySqftFromForm(data.propertySqft),
            }
          : {
              propertyType: undefined,
              stories: undefined,
              yearBuilt: undefined,
              propertySqft: undefined,
            }),
        showEstimatedValueChart: data.showEstimatedValueChart,
        // Card settings ride along so content and visibility commit together.
        // `undefined` means "no password" — the toggle is the intent, and
        // `validateCrossFields` already rejected a blank code while it's on.
        accessCode: data.requireAccessCode ? data.accessCode.trim() : undefined,
        browsable: data.browsable,
      });

      // Re-seed the baseline so `isDirty` clears; the saved values are now the
      // reference point for "unsaved changes".
      form.reset(data);
      await pageQuery.refetch();
      toaster({
        message: result.isAccessProtected
          ? "Saved. Your page inherits your card's password."
          : "Your investor page is live.",
        variant: "success",
      });
    } catch (error) {
      // `saveTtwPage` throws `SenderError` with a human-readable message for
      // every validation failure, so surface it rather than a generic string.
      ErrorHandler.default(error);
    }
  }, onInvalid);

  const exit = () => {
    if (form.formState.isDirty && !window.confirm("Discard unsaved changes?")) return;
    // Carry the brand back so a multi-brand owner lands on the dashboard for the
    // business they were just editing, not on their first one.
    void navigate({ to: "/profile/smb", search: brandSearch });
  };

  // Hold the loader until BOTH seeds are available. The form is seeded once, and
  // only after both queries settle (see `seeded` above); rendering the editor
  // before then would show an unseeded form — briefly, but long enough for an
  // owner to toggle something against the wrong baseline.
  if (pageQuery.isLoading || superViewQuery.isLoading) return <FullPageLoader />;

  if (pageQuery.isError) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-2xl flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="withus-strong text-2xl">Something went wrong</h1>
        <p>We couldn't load your page. Please try again.</p>
        <Button
          color="blue"
          size="md"
          onClick={() => void pageQuery.refetch()}
          className="rounded-none font-sans"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh bg-withus-white">
      {/* ===== Editing rail ===== */}
      <aside
        className={cn(
          "shrink-0 border-r border-withus-dark-blue/10 bg-withus-pure-white transition-[width] duration-200",
          // Collapsed on desktop shrinks to zero; on mobile the rail is the
          // whole screen and the preview is reachable by collapsing it.
          collapsed ? "w-0 overflow-hidden" : "w-full lg:w-[360px]",
        )}
      >
        <div className="sticky top-0 flex max-h-dvh flex-col overflow-y-auto p-6">
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exit}
              className="rounded-none border border-withus-dark-blue/10 text-withus-dark-blue hover:bg-withus-dark-blue/[0.03]"
            >
              Exit composer
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse editor"
              className="size-9 shrink-0 rounded-none border border-withus-dark-blue/10 text-withus-dark-blue hover:bg-withus-dark-blue/[0.03]"
            >
              <ArrowLeftToLineIcon aria-hidden />
            </Button>
          </div>

          {/* Controlled, because the tab is also set programmatically: a
              validation failure jumps to whichever tab owns the offending field
              (see `onInvalid` / `FIELD_TAB`). */}
          <Tabs value={tab} onValueChange={(next) => setTab(next as TabId)} className="mt-10">
            {/* The design draws plain underlined text here, not the boxed
                segmented control `TabsList` defaults to — so the list's border
                and padding are stripped and the triggers restyled. The
                primitive is still doing the real work: roving arrow-key focus
                and the tablist/tab/tabpanel relationships. */}
            <TabsList className="h-auto w-full justify-start gap-4 border-none bg-transparent p-0">
              {TABS.map((t) => (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  className="h-auto flex-none px-0 py-0 text-sm font-medium opacity-60 data-[state=active]:bg-transparent data-[state=active]:text-withus-dark-blue data-[state=active]:underline data-[state=active]:underline-offset-4 data-[state=active]:opacity-100"
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="overview" className="mt-10">
              <OverviewTab form={form} blobs={blobsRef.current} onAssetUploaded={onAssetUploaded} />
            </TabsContent>
            <TabsContent value="steward" className="mt-10">
              <StewardTab form={form} blobs={blobsRef.current} onAssetUploaded={onAssetUploaded} />
            </TabsContent>
            <TabsContent value="real-estate" className="mt-10">
              <RealEstateTab
                form={form}
                blobs={blobsRef.current}
                onAssetUploaded={onAssetUploaded}
                properties={properties}
                staleTargetPropertyAddress={
                  staleTargetProperty
                    ? formatCanonicalAddressFull(staleTargetProperty.canonicalAddress)
                    : undefined
                }
                needsTargetArea={values.targetProperty === ""}
              />
            </TabsContent>
            <TabsContent value="settings" className="mt-10">
              <SettingsTab form={form} />
            </TabsContent>
          </Tabs>

          {/* Save is an addition to the design, which draws no save control.
              An explicit commit needs an explicit affordance. */}
          <div className="sticky bottom-0 -mx-6 mt-8 border-t border-withus-dark-blue/10 bg-withus-pure-white px-6 py-4">
            <Button
              color="blue"
              size="md"
              onClick={() => void onSave()}
              disabled={isSaving || !propertiesReady || !settingsReady}
              className="w-full rounded-none font-sans"
            >
              {!propertiesReady || !settingsReady
                ? "Loading…"
                : isSaving
                  ? "Saving…"
                  : form.formState.isDirty || !saved?.hasPage
                    ? // A never-saved page is not "Saved" even before it's dirty —
                      // the label has to invite the first publish, not claim a
                      // write that never happened.
                      "Save changes"
                    : "Saved"}
            </Button>
          </div>
        </div>
      </aside>

      {/* ===== Live preview ===== */}
      <main className={cn("min-w-0 flex-1", collapsed ? "block" : "hidden lg:block")}>
        {collapsed ? (
          // In the flow, and deliberately NOT sticky: the preview's investment
          // sidebar is itself `lg:sticky lg:top-6`, so any pinned chrome above it
          // would clip the card as the page scrolls — the exact problem this
          // page had when a sticky section nav sat here. Scrolling away is fine;
          // the control's job is the initial expand.
          <div className="flex justify-start border-b border-withus-dark-blue/10 bg-withus-pure-white p-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCollapsed(false)}
              aria-label="Expand editor"
              className="size-9 rounded-none border border-withus-dark-blue/10 text-withus-dark-blue hover:bg-withus-dark-blue/[0.03]"
            >
              <ArrowRightToLineIcon aria-hidden />
            </Button>
          </div>
        ) : null}
        <PreviewPane content={content} cardId={saved?.cardId} />
      </main>
    </div>
  );
}

/**
 * The live preview: the real public-page renderer, fed from form values.
 *
 * The sidebar reads REAL backer data via `getTtwPageBackers` against the
 * owner's own card — the feed, backer count, and nominator credit are never
 * stubbed here. That's the point of the composer being WYSIWYG: an owner sees
 * the actual community activity on their page, not lorem ipsum.
 *
 * Before the first save there may be no card yet (`cardId` absent), so the
 * sidebar renders without a feed rather than being faked.
 */
function PreviewPane({ content, cardId }: { content: TtwPageContent; cardId: Uuid | undefined }) {
  // The empty `accessCode` is correct here and is NOT relying on route auth:
  // `getTtwPageBackers` exempts the card's owner from the password gate
  // server-side (see `isCardOwner`). It has to — the access code is a shared
  // secret that never crosses the wire to any client, so the composer has
  // nothing to send. Without that bypass an owner with a protected card would
  // preview an empty feed.
  //
  // `enabled` (not `undefined` variables) is what holds the call back before the
  // first save: passing `undefined` would invoke the procedure with NO argument,
  // and `getTtwPageBackers` requires a cardId.
  const backersQuery = useProcedureQueryLazy(
    procedures.getTtwPageBackers,
    { cardId: cardId as Uuid, accessCode: "" },
    { enabled: cardId !== undefined },
  );

  return (
    <TtwPage
      content={content}
      sidebar={
        cardId ? (
          <InvestmentSidebar
            cardId={cardId}
            businessName={content.businessName}
            sharePrice={content.sharePrice}
            targetReturnRange={content.targetReturnRange}
            showTotalReserved={content.showTotalReserved}
            showAppreciationShare={content.showAppreciationShare}
            appreciationSharePercent={content.appreciationSharePercent}
            backers={backersQuery.data}
            onPledged={() => backersQuery.refetch().then(() => undefined)}
          />
        ) : (
          <div className="border border-withus-dark-blue/10 bg-withus-pure-white p-6 text-sm text-withus-dark-blue/60">
            Save your page to see live pledges here.
          </div>
        )
      }
    />
  );
}

// Platform defaults, mirroring `ttwPages/tables.ts`. Used only to preview a page
// that has never been saved — once saved, the stored terms win.
const DEFAULT_SHARE_PRICE = 100;
const DEFAULT_TOTAL_SHARES = 10_000;
// Equal bounds render as a flat "10% target return" rather than a span.
const DEFAULT_RETURN_MIN = 10;
const DEFAULT_RETURN_MAX = 10;
